// FSD lib/ segment: pure calculation helpers for EOB service line fields.
// Lives at page level because these formulas are EOB-specific, not shared across slices.

import type { ServiceLineFormValues } from '../model'

/** Allowed = Billed - Adjustments - OtherPolicy. Returns null when billed is null. */
export function computeAllowedFromDeductions(
  billedAmount: number | null,
  adjustments: number | null,
  otherPlan: number | null,
): number | null {
  if (billedAmount == null) return null
  return billedAmount - (adjustments ?? 0) - (otherPlan ?? 0)
}

/** Whether any payor deduction has a non-null value (used to decide if Allowed is auto-calculated). */
export function hasPayorDeductions(
  sl: Pick<ServiceLineFormValues, 'adjustments' | 'otherPlan'>,
): boolean {
  return (sl.adjustments?.amount ?? 0) !== 0 || (sl.otherPlan ?? 0) !== 0
}

/**
 * When co-insurance is $ (fixed):
 *   Allowed = Billed − Adjustments − Other Policy
 *   Patient Responsibility = Deductible + Co-insurance($) + Copay
 *   Paid = Allowed − Patient Responsibility − Non-Covered
 *
 * When co-insurance is % (percentage):
 *   Allowed = Billed − Adjustments − Other Policy
 *   Co-insurance($) = Allowed × Co-insurance(%)
 *   Patient Responsibility = Deductible + Calculated Co-insurance + Copay
 *   Paid = Allowed − Patient Responsibility − Non-Covered
 */
export function computePaidAmount(sl: ServiceLineFormValues): number {
  const d = (v: ServiceLineFormValues['adjustments'] | undefined) => v?.amount ?? 0

  const allowed =
    sl.allowedAmount ??
    computeAllowedFromDeductions(sl.billedAmount, sl.adjustments?.amount ?? null, sl.otherPlan) ??
    0

  const nonCovered = d(sl.nonCovered)

  if (sl.coInsurance?.amountType === 'percentage') {
    const coInsuranceDollars = Math.round((allowed * (sl.coInsurance.amount ?? 0)) / 100)
    const patientResp = d(sl.deductible) + coInsuranceDollars + d(sl.copayment)
    return allowed - patientResp - nonCovered
  }

  const patientResp = d(sl.deductible) + d(sl.coInsurance) + d(sl.copayment)
  return allowed - patientResp - nonCovered
}

/** totalPaidCents - sum of computePaidAmount for each service line. Returns null when totalPaidCents is null. */
export function computeLeftToAllocate(
  totalPaidCents: number | null,
  serviceLines: ServiceLineFormValues[],
): number | null {
  if (totalPaidCents === null) return null

  const sumPaid = serviceLines.reduce((acc, sl) => acc + computePaidAmount(sl), 0)
  return totalPaidCents - sumPaid
}
